// Full site content for Nador Sage International.

export const BRAND = {
    name: "Nador Sage International",
    legal: "Nador Sage International S.r.l.",
    short: "Nador Sage",
    tagline: "General Trading & Strategic Sourcing",
    description:
        "An independent international trading and strategic sourcing company built on more than two decades of experience in global markets, delivering tailored solutions across diverse industries and regions.",
};

export const CONTACT_EMAILS = ["info@nadorsage.com"];

export const NAV = [
    { to: "/", label: "Home" },
    { to: "/about", label: "About" },
    { to: "/business-areas", label: "Business Areas" },
    { to: "/global-presence", label: "Global Presence" },
    { to: "/values", label: "Values & Approach" },
];

export const BUSINESS_AREAS = [
    {
        slug: "aviation",
        title: "Aviation",
        teaser: "Aircraft, helicopters, engines, and aviation equipment",
        image:
            "https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=1600&q=80",
        intro:
            "Nador Sage International supports the aviation industry through comprehensive trading, sourcing, and maintenance support for aircraft, helicopters, and propulsion systems. We work with operators, MRO facilities, and manufacturers worldwide.",
        scope: [
            "Aircraft and helicopter trading and supplying",
            "Motors, engines, and turbines for aircraft and helicopters",
            "Aircraft and helicopters parts and components procurement",
            "Ground support equipment",
            "MRO supplies and maintenance support",
            "Aviation logistics coordination",
        ],
        focus:
            "We prioritize compliance with aviation standards and maintain rigorous quality assurance processes. Our expertise covers the full lifecycle from trading to maintenance support for both fixed-wing aircraft and rotorcraft.",
        callout: {
            label: "Advisory Practice",
            text:
                "Nador Sage also operates a dedicated Aviation Cargo Advisory & Corporate Recovery practice led by Alessandro Cisselo.",
            ctaLabel: "Read the Advisory Brochure",
            ctaHref: "#/advisory",
        },
    },
    {
        slug: "automotive",
        title: "Automotive",
        teaser: "Cars, trucks, utility vehicles, and special equipment",
        image:
            "https://images.unsplash.com/photo-1655103955676-c9fbc4b65525?w=1600&q=80",
        intro:
            "Nador Sage International operates in the automotive sector through trading, sourcing, and distribution of a comprehensive range of vehicles and related components. We serve manufacturers, dealers, and fleet operators worldwide.",
        scope: [
            "Cars and passenger vehicles",
            "Light utility vehicles and pickups",
            "Trucks and commercial vehicles",
            "Special vehicles and equipment",
            "OEM and aftermarket parts",
            "Automotive components and accessories",
        ],
        focus:
            "We focus on quality assurance, supply chain coordination, and building long-term relationships with manufacturers and distributors across global markets. From standard vehicles to specialized equipment, we provide comprehensive sourcing solutions.",
    },
    {
        slug: "oil-gas",
        title: "Oil & Gas",
        teaser: "Energy commodities and petroleum products",
        image:
            "https://images.unsplash.com/photo-1573153178631-49e3aa9e018b?w=1600&q=80",
        intro:
            "Our energy trading activities encompass crude oil, refined petroleum products, and energy-related commodities. We facilitate supply chain coordination for energy sector partners.",
        scope: [
            "Crude oil trading",
            "Refined petroleum products",
            "Energy commodity sourcing",
            "Supply chain coordination",
        ],
        focus:
            "Compliance, reliability, and strategic partnerships form the foundation of our energy trading operations.",
    },
    {
        slug: "healthcare",
        title: "Healthcare",
        teaser: "Medical equipment and clinical supplies",
        image:
            "https://images.unsplash.com/photo-1766297247072-93fd815afef3?w=1600&q=80",
        intro:
            "Our healthcare division focuses on medical equipment, supplies, and consumables to support hospitals and clinical facilities.",
        scope: [
            "Medical equipment trading",
            "Healthcare consumables",
            "Hospital procurement support",
            "Clinical supplies distribution",
        ],
        focus:
            "Quality, compliance with healthcare regulations, and reliable supply chains are central to our healthcare operations.",
    },
    {
        slug: "food",
        title: "Food Products",
        teaser: "Agricultural and processed food commodities",
        image:
            "https://images.unsplash.com/photo-1574323347407-f5e1ad6d020b?w=1600&q=80",
        intro:
            "We facilitate international sourcing and distribution of agricultural commodities, processed foods, and packaged food products.",
        scope: [
            "Agricultural commodities trading",
            "Processed food distribution",
            "Packaged food sourcing",
            "International food logistics",
        ],
        focus:
            "Food safety compliance, quality standards, and efficient logistics define our food trading operations.",
    },
];

export const ABOUT = {
    eyebrow: "An independent international trading and strategic sourcing company",
    title: "About Nador Sage International",
    sections: [
        {
            heading: "Company Overview",
            body:
                "Nador Sage International is an independent international trading and strategic sourcing company built on more than two decades of experience in global markets, delivering tailored solutions across diverse industries and regions.",
        },
        {
            heading: "Purpose & Role",
            body:
                "We serve as a strategic intermediary in global supply chains, facilitating cross-border trade, procurement, and distribution. Our role extends beyond transactional trading to building long-term partnerships with manufacturers, distributors, and end-users.",
        },
        {
            heading: "Our Approach",
            body:
                "Nador Sage International takes a long-term orientation in all business relationships. We prioritize reliability, discretion, and full compliance with applicable regulations and standards. Our operations are structured to support partners across different jurisdictions and market conditions.",
        },
        {
            heading: "Our Commitment",
            body:
                "We are committed to responsible business conduct, transparent operations, and maintaining the trust of our partners and stakeholders.",
        },
    ],
    image:
        "https://images.unsplash.com/photo-1721995432582-b0a486848fca?w=1600&q=80",
};

export const GLOBAL_PRESENCE = {
    eyebrow: "Cross-border trade and strategic partnerships",
    title: "Global Presence",
    image:
        "https://images.unsplash.com/photo-1526778548025-fa2f459cd5c1?w=1600&q=80",
    regionsLead:
        "Nador Sage International operates across international markets, with established networks and business relationships throughout Asia, Europe, and the Middle East. Through our global reach, we facilitate strategic sourcing, trade development, and logistics coordination for clients and partners worldwide, including North America, Central America, South America, and Africa.",
    regions: [
        "Asia",
        "Europe",
        "Middle East",
        "North America",
        "Central America",
        "South America",
        "Africa",
    ],
    capabilities: [
        "Cross-border trade facilitation",
        "Multi-jurisdiction compliance",
        "Logistics and supply chain coordination",
        "Strategic sourcing partnerships",
    ],
    modelHeading: "Relationship-Based Model",
    modelBody:
        "Our trading model is built on relationships and long-term partnerships. We work closely with partners to understand their specific requirements and provide tailored sourcing solutions.",
};

export const VALUES = {
    eyebrow: "The principles that guide our operations",
    title: "Values & Approach",
    image:
        "https://images.unsplash.com/photo-1726419181261-0f1bc228233b?w=1600&q=80",
    items: [
        {
            heading: "Integrity",
            body:
                "We conduct all business with honesty and transparency, maintaining the highest ethical standards in every transaction.",
        },
        {
            heading: "Reliability",
            body:
                "Our partners trust us to deliver consistent, dependable service. We fulfill our commitments and stand behind our work.",
        },
        {
            heading: "Compliance",
            body:
                "We operate in full compliance with applicable laws, regulations, and industry standards across all jurisdictions.",
        },
        {
            heading: "Long-term Partnerships",
            body:
                "We build enduring relationships with our partners, prioritizing mutual success over short-term gains.",
        },
        {
            heading: "Responsible Conduct",
            body:
                "We are committed to responsible business practices that consider the impact on communities and the environment.",
        },
    ],
};

export const HOME = {
    eyebrow: BRAND.tagline,
    title: BRAND.name,
    description: BRAND.description,
    statRegions: 7,
    statSectors: 5,
    approach: ["Integrity", "Reliability", "Compliance"],
    approachLead:
        "Built on integrity, reliability, and long-term partnerships. We support our partners with compliant, transparent operations.",
    images: [
        "https://images.unsplash.com/photo-1494412574643-ff11b0a5c1c3?w=1200&q=80",
        "https://images.unsplash.com/photo-1726419303783-28a408a81b48?w=1200&q=80",
    ],
};

export const OFFICES = [
    {
        label: "Registered Office",
        city: "Bucharest, Romania",
        address: "Lămâiului Street, 4",
    },
    {
        label: "Europe Office & Depot",
        city: "Bucharest, Romania",
        address: "CTP Complex Ion Ratiu Street, 5",
    },
    {
        label: "Italian Desk and Administration",
        city: "Sanremo, Italy",
        address: "Hope Street, 6",
        phones: [
            { kind: "Landline", number: "+39 0184.63.66.41" },
            { kind: "Mobile", number: "+39 333.366.89.10" },
        ],
    },
    {
        label: "Central Asia",
        city: "Tashkent, Uzbekistan",
        address: "Yusupov Street Block 4",
        phones: [
            { kind: "Landline", number: "+998 777.20.09.30" },
            { kind: "Mobile", number: "+998 777.20.09.31" },
        ],
    },
];
